import {DateFormat} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Typography} from '@mui/material'
import {TFunction} from 'i18next'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, NO_VALUE_STRING} from '../consts'
import {SystemChangeRequestTableItem} from '../types'

type Props = {
  data: SystemChangeRequestTableItem[]
  onRowClick?: (id: string) => void
  showTableLoading?: boolean
  plantId: string
}

export const SystemChangeRequestTable = ({data, onRowClick, showTableLoading, plantId}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const columns = useMemo(() => generateColumns({t, timezone}), [t, timezone])

  return (
    <DataTable
      data={data}
      columns={columns}
      onRowClick={(event, {id}) => {
        event.stopPropagation()
        event.preventDefault()
        onRowClick?.(id)
      }}
      keyExtractor={(item) => item.id}
      data-test-id="system-change-request-table"
      loading={showTableLoading}
    />
  )
}

const generateColumns = ({
  t,
  timezone
}: {
  t: TFunction
  timezone: string
}): Column<SystemChangeRequestTableItem>[] => [
  {
    key: 'title',
    label: `${t('systemChangeRequestTable.label.title')}/${t(
      'systemChangeRequestTable.label.function'
    )}`,
    customTemplate: ({title, functionName}) => (
      <>
        <Typography data-test-id="title">{title}</Typography>
        <Typography variant="body2" color="secondary" data-test-id="functionName">
          {functionName}
        </Typography>
      </>
    )
  },
  {
    key: 'validity',
    label: t('systemChangeRequestTable.label.validity'),
    customTemplate: ({validFrom, validTo}) => {
      if (!validFrom) return NO_VALUE_STRING
      if (!validTo) {
        return <DateFormat date={validFrom} timezone={timezone} />
      }
      return (
        <>
          <DateFormat date={validFrom} timezone={timezone} />
          {' - '}
          <DateFormat date={validTo} timezone={timezone} />
        </>
      )
    }
  }
]
